import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Search, Send, UserMinus } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Avatar } from '@/components/ui/Avatar';
import { toast } from '@/components/ui/Toaster';
import { cn } from '@/lib/utils';
import { formsApi, type Form, type FormResponse } from '@/lib/api';
import { DueBadge, ResponseStatusPill } from './FormListRow';
import { formatDateShort } from './form-utils';

interface AssignPanelProps {
  form: Form;
  responses: FormResponse[];
  onChanged: () => void;
  onOpenResponse: (responseId: string) => void;
}

/** Pick people, set a due date, send. Below: who has it and where they are with it. */
export function AssignPanel({ form, responses, onChanged, onOpenResponse }: AssignPanelProps) {
  const queryClient = useQueryClient();
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const [dueDate, setDueDate] = useState('');
  const [note, setNote] = useState('');
  const published = form.status === 'published';
  const archivedProject = !!form.project?.archivedAt;

  const { data: users, isLoading: usersLoading } = useQuery({
    queryKey: ['formAssignableUsers', form.projectId ?? 'global', form.projectId ? '' : query],
    queryFn: () => formsApi.assignableUsers(form.projectId, form.projectId ? undefined : query),
  });

  const openByUser = useMemo(() => new Set(responses.filter((r) => r.status !== 'submitted').map((r) => r.userId)), [responses]);

  const visibleUsers = useMemo(() => {
    const list = users ?? [];
    if (!form.projectId || !query.trim()) return list;
    const q = query.toLowerCase();
    return list.filter((u) => (u.name || '').toLowerCase().includes(q) || u.email.toLowerCase().includes(q));
  }, [users, query, form.projectId]);

  const assignMutation = useMutation({
    mutationFn: () => formsApi.assign(form.id, { userIds: selected, dueDate: dueDate ? new Date(`${dueDate}T12:00:00`).toISOString() : undefined, note: note.trim() || undefined }),
    onSuccess: ({ created, skipped }) => {
      toast({
        type: 'success',
        title: created.length === 1 ? 'Form sent to 1 person' : `Form sent to ${created.length} people`,
        message: skipped.length ? `${skipped.length} already had it open and were skipped.` : undefined,
      });
      setSelected([]);
      setNote('');
      queryClient.invalidateQueries({ queryKey: ['myForms'] });
      onChanged();
    },
    onError: (e: any) => toast({ type: 'error', title: "Couldn't assign the form", message: e?.message }),
  });

  const unassignMutation = useMutation({
    mutationFn: (responseId: string) => formsApi.deleteResponse(form.id, responseId),
    onSuccess: () => onChanged(),
    onError: (e: any) => toast({ type: 'error', title: "Couldn't remove the assignment", message: e?.message }),
  });

  const dueMutation = useMutation({
    mutationFn: ({ responseId, value }: { responseId: string; value: string }) =>
      formsApi.updateAssignment(form.id, responseId, { dueDate: value ? new Date(`${value}T12:00:00`).toISOString() : null }),
    onSuccess: () => onChanged(),
    onError: (e: any) => toast({ type: 'error', title: "Couldn't change the due date", message: e?.message }),
  });

  const toggle = (id: string) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const sorted = useMemo(
    () =>
      [...responses].sort((a, b) => {
        const order = { pending: 0, draft: 1, submitted: 2 } as const;
        if (order[a.status] !== order[b.status]) return order[a.status] - order[b.status];
        return (a.user?.name || a.user?.email || '').localeCompare(b.user?.name || b.user?.email || '');
      }),
    [responses],
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
      <div className="lg:col-span-5">
        <div className="card p-4 space-y-4">
          <div>
            <h3 className="text-sm font-semibold text-secondary">Send to</h3>
            <p className="text-xs text-grey-500 mt-0.5">{form.projectId ? 'Members of this project.' : 'Anyone who has signed in to the Hub.'} Each person gets an email and a to-do.</p>
          </div>

          {!published && (
            <p className="text-xs text-warning bg-warning/10 rounded-lg px-3 py-2">This form is {form.status}. Publish it before assigning — people can't open a draft.</p>
          )}

          <div className="relative">
            <Search className="w-4 h-4 text-grey-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search people…" className="input w-full pl-9 py-2" aria-label="Search people" />
          </div>

          <div className="max-h-64 overflow-y-auto -mx-1 px-1 space-y-0.5">
            {usersLoading ? (
              <div className="flex justify-center py-6">
                <Loader2 className="w-5 h-5 text-grey-400 animate-spin" />
              </div>
            ) : visibleUsers.length === 0 ? (
              <p className="text-sm text-grey-500 py-4 text-center">{query ? 'No one matches.' : 'No one to assign yet.'}</p>
            ) : (
              visibleUsers.map((u) => {
                const already = openByUser.has(u.id);
                const checked = selected.includes(u.id);
                return (
                  <label key={u.id} className={cn('flex items-center gap-3 px-2 py-1.5 rounded-lg cursor-pointer hover:bg-grey-50', already && 'opacity-60')}>
                    <input type="checkbox" checked={checked} onChange={() => toggle(u.id)} disabled={already} className="w-4 h-4 rounded text-accent border-grey-300 focus:ring-accent" />
                    <Avatar name={u.name || u.email} src={u.avatarUrl || undefined} size="sm" />
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm text-secondary truncate">{u.name || u.email}</span>
                      {u.name && <span className="block text-xs text-grey-500 truncate">{u.email}</span>}
                    </span>
                    {already && <span className="text-xs text-grey-400 shrink-0">Has it</span>}
                    {u.isStaff && !already && <span className="text-xs text-grey-400 shrink-0">Staff</span>}
                  </label>
                );
              })
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label htmlFor="assign-due" className="block text-xs font-medium text-grey-600 mb-1">
                Due date (optional)
              </label>
              <input id="assign-due" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="input w-full py-2" />
            </div>
          </div>
          <div>
            <label htmlFor="assign-note" className="block text-xs font-medium text-grey-600 mb-1">
              Note to the assignee (optional)
            </label>
            <textarea id="assign-note" rows={2} value={note} onChange={(e) => setNote(e.target.value)} className="input w-full" placeholder="Anything they should know before they start." />
          </div>

          <Button onClick={() => assignMutation.mutate()} disabled={selected.length === 0 || !published || archivedProject} isLoading={assignMutation.isPending} className="w-full">
            <Send className="w-4 h-4 mr-2" />
            {selected.length ? `Send to ${selected.length} ${selected.length === 1 ? 'person' : 'people'}` : 'Send'}
          </Button>
        </div>
      </div>

      <div className="lg:col-span-7">
        <div className="card p-0 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-grey-100 bg-grey-50/50">
            <span className="text-sm font-medium text-secondary">Who has it</span>
            <span className="text-xs text-grey-400 tabular-nums">({responses.length})</span>
          </div>
          {sorted.length === 0 ? (
            <p className="px-4 py-8 text-sm text-grey-500 text-center">{form.openSubmissions ? 'No one has started yet. Members can open it from the project.' : 'Nobody yet — pick people on the left.'}</p>
          ) : (
            <ul className="divide-y divide-grey-100">
              {sorted.map((r) => (
                <li key={r.id} className="flex items-center gap-3 px-4 py-3">
                  <Avatar name={r.user?.name || r.user?.email || '?'} src={r.user?.avatarUrl || undefined} size="sm" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-secondary truncate">{r.user?.name || r.user?.email}</span>
                      <ResponseStatusPill status={r.status} />
                      {!r.assignedById && <span className="text-xs text-grey-400">self-started</span>}
                    </div>
                    <div className="flex items-center gap-3 flex-wrap text-xs text-grey-500 mt-0.5">
                      {r.status === 'submitted' ? (
                        <span>Submitted {formatDateShort(r.submittedAt)}</span>
                      ) : (
                        <>
                          <DueBadge dueDate={r.dueDate} />
                          <label className="inline-flex items-center gap-1">
                            <span className="sr-only">Due date for {r.user?.name || r.user?.email}</span>
                            <input
                              type="date"
                              defaultValue={r.dueDate ? r.dueDate.slice(0, 10) : ''}
                              onBlur={(e) => {
                                const v = e.target.value;
                                const current = r.dueDate ? r.dueDate.slice(0, 10) : '';
                                if (v !== current) dueMutation.mutate({ responseId: r.id, value: v });
                              }}
                              className="text-xs border border-grey-200 rounded px-1.5 py-0.5 text-grey-600"
                            />
                          </label>
                        </>
                      )}
                      {r.assignedBy && <span>by {r.assignedBy.name || r.assignedBy.email}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {r.status === 'submitted' ? (
                      <button type="button" onClick={() => onOpenResponse(r.id)} className="btn-ghost btn-sm">
                        View
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => {
                          if (window.confirm(`Remove ${r.user?.name || r.user?.email} from this form?${r.status === 'draft' ? ' Their draft answers will be deleted.' : ''}`)) unassignMutation.mutate(r.id);
                        }}
                        className="p-1.5 text-grey-400 hover:text-error rounded transition-colors"
                        aria-label="Unassign"
                        title="Unassign"
                      >
                        <UserMinus className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
